import { ApiResponse, axiosInstance } from '@/lib/axios';
import { FetchParams, IPagination } from '@/types/general.type';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { TChannel } from '../../../_shared/types/channel.type';

export type TGetTiktokChannelsResponse = ApiResponse<IPagination<TChannel>>;
export type TGetTiktokChannelsParams = FetchParams;

export const getTiktokChannels = async (params: TGetTiktokChannelsParams): Promise<TGetTiktokChannelsResponse> => {
  const url = '/integration/tiktok/org-channel';

  return axiosInstance.get(url, { params });
};

export const GET_TIKTOK_CHANNELS_QUERY_KEY = 'get-list-tiktok-channels';

export const getTiktokChannelsQueryOptions = <TData = IPagination<TChannel>>(
  params: TGetTiktokChannelsParams,
  select?: (data: TGetTiktokChannelsResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_TIKTOK_CHANNELS_QUERY_KEY,
      {
        page: params?.page,
        limit: params?.limit,
        search: params?.search,
        sortDir: params?.sortDir,
      },
    ],
    queryFn: () => getTiktokChannels(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useGetTiktokChannelsQuery<TData>(options: {
  params: TGetTiktokChannelsParams;
  queryConfig?: QueryConfig<typeof getTiktokChannelsQueryOptions<TData>>;
  select: (data: TGetTiktokChannelsResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useGetTiktokChannelsQuery(options: {
  params: TGetTiktokChannelsParams;
  queryConfig?: QueryConfig<typeof getTiktokChannelsQueryOptions<TGetTiktokChannelsResponse>>;
}): ReturnType<typeof useQuery<TGetTiktokChannelsResponse>>;

// Implementation
export function useGetTiktokChannelsQuery<TData = TGetTiktokChannelsResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TGetTiktokChannelsParams;
  queryConfig?: QueryConfig<typeof getTiktokChannelsQueryOptions<TData | TGetTiktokChannelsResponse>>;
  select?: (data: TGetTiktokChannelsResponse) => TData;
}) {
  return useQuery({
    ...getTiktokChannelsQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<typeof useQuery<TData extends TGetTiktokChannelsResponse ? TGetTiktokChannelsResponse : TData>>;
}
